import {
  ACCESSIBILITY_ARGS,
  ANCHORED_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  INPUT_MENU_ARGS,
  MENU_PLACEMENT_ARG,
  Metadata,
  SIZING_ARGS,
  pick,
} from '../../../../utils/spds/metadata';
import {
  ANCHOR_WIDTH_PROPERTY,
  MENU_ITEM_SELECTOR,
  MENU_MAX_HEIGHT,
  SIZING_PROPERTIES,
} from '../../../../utils/spds/constants/menu';
import { ROLE_LISTBOX } from '../../../../utils/spds/constants/aria';
import { TOP } from '../../../../utils/spds/constants/positioning';
import { fn, hash } from '@ember/helper';
import { modifier } from 'ember-modifier';
import { parsePlacement } from '../../../../utils/spds/positioning';
import { resolveLength, toCssLength } from '../../../../utils/spds/css/length';
import { tracked } from '@glimmer/tracking';
import FlexColumn from '../../layout/flex/column.gjs';
import Footer from './footer.gjs';
import Header from './header.gjs';
import NoResults from './no-results.gjs';
import Super from '../popover.gjs';
import VariableLayout from '../../layout/variable.gjs';
import styles from './base.module.scss';

const List = <template>
  <VariableLayout
    @column={{@column}}
    @gap="4px"
    @id={{@id}}
    @registerComponent={{@registerListComponent}}
    @unregisterComponent={{@unregisterListComponent}}
    aria-labelledby={{@ariaLabelledBy}}
    class="spds-menu-list"
    data-test-spds-menu-list
    role={{@role}}
  >
    {{yield}}

    {{#if @showNoResults}}
      <NoResults @text={{@noResultsText}} />
    {{/if}}
  </VariableLayout>
</template>;

const METADATA = Metadata.define({
  description: `
Simple, easy to use menu component. This is the primitive menu shell: it handles positioning and
renders a scrollable list of the items yielded to it, plus an optional no-results state and an
optional footer. For an in-menu search field that filters items as the user types, use
\`Spds::Menu::Filterable\`, which extends this component.

### Positioning

The menu is a \`popover="manual"\` element, so it paints in the browser's top layer. It is not
clipped by the overflow of a modal, flyout, or scroll container it happens to live inside, and it
does not need a \`z-index\`. It stays where it is in the DOM, so descendant CSS selectors and
click-outside handling are unaffected. Whether the menu is showing is the \`open\` class rather
than the browser's popover state, so browsers predating the Popover API open and close it the same
way; what they lose is the top layer, leaving the menu clipped by those same ancestors.

\`@anchorElement\` is the DOM element the menu positions itself against, and \`@anchorOffset\`
sets the gap between them as a CSS length, \`4px\` by default. Because a top-layer popover is
positioned against the viewport rather than a parent element, the menu measures the anchor on open
and tracks it while scrolling or resizing.

Because alignment and the menu's default width are both measured from \`@anchorElement\`, it should
be the element the menu is meant to line up with, which for a compound control is the control's
root rather than the part that opens the menu. Anchoring to a sub-part looks correct while the menu
is left-aligned and goes wrong as soon as it is not.

Those measurements are published to the element as custom properties and composed in the
stylesheet: the menu sits at the anchor edge (\`--spds-popover-anchor-top\`,
\`--spds-popover-anchor-left\`) and the gap (\`--spds-popover-anchor-offset\`) opens it from there.
The browser resolves the gap, so \`@anchorOffset\` accepts any CSS length. All of that is
\`Spds::Core::Popover\`, which the menu extends.

\`@placement\` sets where the menu prefers to sit, as \`<side>-<alignment>\`: the side of the anchor
it opens on (\`bottom\` or \`top\`) and the anchor edge it aligns to (\`left\`, \`middle\`, or
\`right\`). It defaults to \`bottom-left\`, the dropdown position.

A placement is only a starting point. If the menu would run off the edge of the viewport it
corrects itself, flipping to the other side of the anchor when it would overflow vertically and
shifting its alignment when it would overflow horizontally. The placement it settles on is
published to the element as the \`flipped\` class plus the resolved alignment (\`left\`,
\`middle\`, or \`right\`), which is what turns the entry animation around when the menu opens
upward.

Side placements (\`left\`, \`right\`) are deliberately not offered. The menu defaults to its
anchor's width, which only reads as a dropdown above or below the anchor, and the horizontal
correction above has no vertical counterpart — a tall menu placed beside its anchor would hang off
the viewport with no way to recover.

### Sizing

By default the menu matches the width of its anchor. That can be overridden with \`@width\` and
bounded with \`@minWidth\` / \`@maxWidth\`, or replaced entirely with \`@fitContent\`, which sizes
the menu to its widest item while still respecting those bounds. \`@maxHeight\` caps the scrollable
list, which defaults to 368px.

Sizing and the anchor gap are arguments rather than a styling hook. Each writes its custom property
(\`--spds-menu-width\`, \`--spds-menu-min-width\`, \`--spds-menu-max-width\`,
\`--spds-menu-max-height\`, \`--spds-popover-anchor-offset\`) inline on the menu, where no app
stylesheet can outrank it. Components that render a menu rather than yield it forward these as
\`@menuWidth\`, \`@menuMinWidth\`, \`@menuMaxWidth\`, \`@menuMaxHeight\`, and \`@menuAnchorOffset\`,
so a value that varies with app state is passed in rather than styled around.

### No results

When the consumer indicates the menu is empty via \`@noItemsDisplayed\`, a no-results message (customizable via \`@noResultsText\`)
is shown in place of the list.

### Footer

An optional \`:footer\` named block renders pinned below the scrollable list, outside the scroll region. Consumers that need
persistent controls (for example, apply/clear actions) can render them here.
  `,
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'role', default: ROLE_LISTBOX }, 'ariaLabelledBy']),
    pick(ANCHORED_ARGS, [
      { name: 'anchorElement', description: 'Element to position the menu relative to' },
      { name: 'anchorOffset', default: '4px' },
      MENU_PLACEMENT_ARG,
    ]),
    pick(COMPONENT_STATE_ARGS, ['open', { name: 'animated', default: true }]),
    pick(CONTAINER_ARGS, ['fitContent', { name: 'column', default: true }]),
    pick(INPUT_MENU_ARGS, ['noItemsDisplayed', 'noResultsText']),
    pick(SIZING_ARGS, [
      { name: 'maxHeight', default: MENU_MAX_HEIGHT },
      'maxWidth',
      'minWidth',
      'width',
    ]),
  ],
  name: 'Menu Base',
});

export default class SpdsCoreMenuBase extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu';
  styles = styles;

  @tracked listComponent;

  get classesToMap() {
    return [...super.classesToMap, 'flipped', 'open', 'side'];
  }

  /** The authored gap, as a CSS length. Published for the stylesheet to apply. */
  get anchorOffset() {
    return toCssLength(this.argOrDefault('anchorOffset'));
  }

  /**
   * The same gap in pixels. The stylesheet opens the gap, but the placement math still has to know
   * how big it is: whether the menu fits below its anchor depends on it.
   */
  get anchorDistance() {
    return resolveLength(this.anchorOffset, this.baseElement);
  }

  /** The menu takes the shared anchored placement rules as they come. */
  get anchored() {
    return true;
  }

  /**
   * Drives the upward entry animation and the side the gap opens on, as a styling hook. Nothing
   * timing-sensitive hangs off it — the gap and the entry animation follow the direction custom
   * property, which the component writes directly. See `publishDirection`.
   */
  get flipped() {
    return parsePlacement(this.activePlacement)[0] === TOP;
  }

  /** The edge the menu aligns to, as a class the stylesheet and consuming apps can hang off. */
  get side() {
    return parsePlacement(this.activePlacement)[1];
  }

  get listId() {
    return this.composeSubcomponentId('list');
  }

  get itemElements() {
    if (!this.baseElement) return [];

    return Array.from(this.listComponent?.baseElement?.querySelectorAll(MENU_ITEM_SELECTOR) ?? []);
  }

  get role() {
    return this.argOrDefault('role');
  }

  get showNoResults() {
    return !!this.args.noItemsDisplayed;
  }

  /**
   * Publishes each sizing length so the stylesheet can resolve it. Values are read through
   * `argOrDefault` rather than off `this.args`, so an argument's metadata default reaches the
   * element just like a consumer-supplied value: the metadata stays the single declaration of
   * what the default is, instead of the stylesheet keeping a second copy in a `var()` fallback.
   *
   * `@fitContent` is expressed as `max-content` here rather than through the container's
   * `fit-content-width` class so that it composes with the min/max bounds instead of competing
   * with them for the same declaration.
   */
  #applySizing() {
    let { baseElement } = this;

    for (let [argName, property] of Object.entries(SIZING_PROPERTIES)) {
      let value = this.argOrDefault(argName);

      if (argName === 'width' && this.args.fitContent) value = 'max-content';

      if (value) baseElement.style.setProperty(property, value);
      else baseElement.style.removeProperty(property);
    }
  }

  /**
   * The menu derives its own width from its anchor's, so the width has to be on the element before
   * it measures itself or the rect handed to the placement math is the wrong size.
   *
   * @param {DOMRect} anchorRect
   */
  prepareForMeasurement(anchorRect) {
    this.baseElement.style.setProperty(ANCHOR_WIDTH_PROPERTY, `${anchorRect.width}px`);
  }

  /**
   * Drives the popover from `@open`. This is a modifier rather than `setup()` because it has to
   * re-run every time `@open` flips, and positioning has to happen after `showPopover()` so the
   * measured rect reflects the menu's final size.
   */
  toggleMenuPopover = modifier(() => {
    if (this.fastboot?.isFastBoot) return;

    // Sizing is independent of the open state: keeping it in sync at all times means the menu is
    // already the right size on the frame it opens, so the placement math measures the final rect.
    this.#applySizing();

    if (!this.args.open) {
      this.hidePopover();
      this.stopTrackingAnchor();

      return;
    }

    // The requested placement is the best answer available before the menu can be measured, and the
    // entry animation needs an answer now: `showPopover()` is where the browser captures it.
    // `positionPopover` corrects the property immediately afterwards for the gap and the exit.
    this.publishDirection(this.requestedPlacement);

    this.showPopover();
    this.positionPopover();
    this.startTrackingAnchor();
  });

  <template>
    <FlexColumn
      @fixed={{true}}
      aria-expanded={{@open}}
      class={{this.classes}}
      data-test-spds-menu
      id={{this.id}}
      popover="manual"
      {{this.setupComponent}}
      {{this.toggleMenuPopover}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Body=(component
            List
            ariaLabelledBy=@ariaLabelledBy
            column=@column
            id=this.listId
            noResultsText=@noResultsText
            registerListComponent=(fn this.registerComponent "listComponent")
            role=@role
            showNoResults=this.showNoResults
            unregisterListComponent=(fn this.unregisterComponent "listComponent")
          )
          Footer=Footer
          Header=Header
          listId=this.listId
        )
      }}
    </FlexColumn>
  </template>
}

export const SPDS_CORE_MENU_BASE_METADATA = SpdsCoreMenuBase.metadata;
